/**
 * "Set up from a contract" (threads/customer-billing-budget/mockup-access.html §5).
 *
 * Drop in the contract; GWC finds each "not to exceed" amount, the period and
 * what it covers, and shows the exact sentence it read. Every number is
 * editable and nothing is saved until someone clicks Create. No contract?
 * Start from what last year actually billed.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Loader2, Upload } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CATEGORY_LABEL, money, type BillingResponse, type WorkCategory } from "./model";

interface Found {
    name: string;
    amountCents: number;
    categories: WorkCategory[];
    periodStart: string | null;
    periodEnd: string | null;
    sentence: string;
    missing: string[];
}
interface Reading {
    fileName: string;
    noText: boolean;
    budgets: Found[];
    extras: string[];
}
interface Suggest {
    period: { start: string; end: string; label: string };
    options: Array<{ label: string; name: string; categories: WorkCategory[]; amountCents: number }>;
}
interface Draft {
    key: string;
    on: boolean;
    name: string;
    amount: string;
    start: string;
    end: string;
    categories: WorkCategory[];
    source: string;
}

const THRESHOLDS = [75, 90, 100];

export function ContractDialog({ open, onClose, data }: { open: boolean; onClose: () => void; data: BillingResponse }) {
    const qc = useQueryClient();
    const fileRef = useRef<HTMLInputElement>(null);
    const [reading, setReading] = useState<Reading | null>(null);
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [people, setPeople] = useState<string[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const thisYear = data.presets.find((p) => p.key === "this-year");

    const suggest = useQuery({
        queryKey: ["budget-suggest", data.customer.id],
        queryFn: () => api.get<Suggest>(`/api/customers/${data.customer.id}/budgets/suggest`),
        enabled: open,
    });

    const read = useMutation({
        mutationFn: (file: File) => {
            const fd = new FormData();
            fd.append("file", file);
            return api.postForm<Reading>(`/api/customers/${data.customer.id}/budgets/read-contract`, fd);
        },
        onSuccess: (r) => {
            setReading(r);
            setDrafts(
                r.budgets.map((b, i) => ({
                    key: `c${i}`,
                    on: b.missing.length === 0,
                    name: b.name,
                    amount: String(b.amountCents / 100),
                    start: b.periodStart ?? "",
                    end: b.periodEnd ?? "",
                    categories: b.categories,
                    source: `“${b.sentence}”`,
                })),
            );
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't read that file.");
        },
    });

    const addSuggestion = (o: Suggest["options"][number]) =>
        setDrafts((ds) => [
            ...ds,
            {
                key: `s${ds.length}`,
                on: true,
                name: o.name,
                amount: String(Math.round(o.amountCents / 100)),
                start: suggest.data?.period.start ?? thisYear?.start ?? "",
                end: suggest.data?.period.end ?? thisYear?.end ?? "",
                categories: o.categories,
                source: `${o.label}: ${money(o.amountCents)}`,
            },
        ]);

    const patch = (key: string, p: Partial<Draft>) =>
        setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...p } : d)));
    const amountOf = (d: Draft) => Number(d.amount.replace(/[$,\s]/g, ""));
    const chosen = drafts.filter((d) => d.on);
    const ready =
        chosen.length > 0 &&
        chosen.every((d) => d.name.trim() && amountOf(d) > 0 && d.start && d.end && d.end >= d.start);

    const create = useMutation({
        mutationFn: async () => {
            for (const d of chosen) {
                const note = [
                    reading && d.key.startsWith("c") ? `From ${reading.fileName}: ${d.source}` : d.source,
                    reading?.extras.length && d.categories.includes("testing")
                        ? `Also in the contract: ${reading.extras.join(", ")}.`
                        : null,
                ]
                    .filter(Boolean)
                    .join("\n");
                await api.post(`/api/customers/${data.customer.id}/budgets`, {
                    name: d.name.trim(),
                    amount: amountOf(d),
                    periodStart: d.start,
                    periodEnd: d.end,
                    categories: d.categories,
                    alertThresholds: THRESHOLDS,
                    alertUserIds: people,
                    notes: note.slice(0, 2000) || null,
                });
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer-billing", data.customer.id] });
            qc.invalidateQueries({ queryKey: ["budget-chip", data.customer.id] });
            toast.success(chosen.length === 1 ? "Budget created." : `${chosen.length} budgets created.`);
            onClose();
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't create the budgets.");
        },
    });

    const onFile = (f: File | undefined) => {
        if (f) read.mutate(f);
    };

    return (
        <Dialog open={open} onClose={onClose} title={`Set up budgets for ${data.customer.name ?? "this customer"}`}>
            <div className="space-y-4 text-sm">
                <div
                    role="button"
                    tabIndex={0}
                    data-testid="contract-drop"
                    onClick={() => fileRef.current?.click()}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        onFile(e.dataTransfer.files[0]);
                    }}
                    className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 transition-colors",
                        dragOver ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
                    )}
                >
                    {read.isPending ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : reading ? (
                        <FileText className="h-5 w-5 text-muted-foreground" />
                    ) : (
                        <Upload className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                        <div className="font-semibold">
                            {read.isPending
                                ? "Reading…"
                                : reading
                                  ? reading.fileName
                                  : "Drop the contract here, or click to pick it"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {reading
                                ? reading.noText
                                    ? "This looks like a scan with no text to read — type the numbers below, or start from last year."
                                    : reading.budgets.length === 0
                                      ? "No “not to exceed” amount found. Start from last year, or use Set a budget."
                                      : `Found ${reading.budgets.length} budget${reading.budgets.length === 1 ? "" : "s"}. Check each number against the sentence it came from.`
                                : "PDF, Word (.docx) or text. GWC finds each “not to exceed” amount; nothing is saved until you click Create."}
                        </div>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                        className="hidden"
                        onChange={(e) => onFile(e.target.files?.[0])}
                    />
                </div>

                {drafts.length > 0 && (
                    <div className="space-y-2.5" data-testid="contract-found">
                        {drafts.map((d) => (
                            <div
                                key={d.key}
                                className={cn(
                                    "rounded-lg border p-3",
                                    d.on ? "bg-background" : "bg-muted/30 opacity-70",
                                )}
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={d.on}
                                        onChange={(e) => patch(d.key, { on: e.target.checked })}
                                        aria-label={`Create ${d.name}`}
                                        className="h-4 w-4"
                                    />
                                    <Input
                                        value={d.name}
                                        onChange={(e) => patch(d.key, { name: e.target.value })}
                                        className="h-8 min-w-0 flex-1 font-semibold"
                                        aria-label="Budget name"
                                    />
                                    <Input
                                        value={d.amount}
                                        onChange={(e) => patch(d.key, { amount: e.target.value })}
                                        className="h-8 w-32 text-right tabular-nums"
                                        aria-label="Amount"
                                        inputMode="decimal"
                                    />
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                    <Input
                                        type="date"
                                        value={d.start}
                                        onChange={(e) => patch(d.key, { start: e.target.value })}
                                        className={cn("h-7 w-36", !d.start && "border-amber-500")}
                                        aria-label="Starts"
                                    />
                                    <span>to</span>
                                    <Input
                                        type="date"
                                        value={d.end}
                                        onChange={(e) => patch(d.key, { end: e.target.value })}
                                        className={cn("h-7 w-36", !d.end && "border-amber-500")}
                                        aria-label="Ends"
                                    />
                                    <span className="text-muted-foreground">
                                        Counts:{" "}
                                        {d.categories.length === 0
                                            ? "everything billed"
                                            : d.categories.map((c) => CATEGORY_LABEL[c]).join(" + ")}
                                    </span>
                                </div>
                                {(!d.start || !d.end) && (
                                    <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                                        The contract didn't say which period — pick the dates.
                                    </div>
                                )}
                                <div className="mt-2 border-l-2 pl-2 text-[12px] italic text-muted-foreground">
                                    {d.source}
                                </div>
                            </div>
                        ))}
                        {reading && reading.extras.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                                Also found: {reading.extras.join(", ")} — kept in the testing budget's notes.
                            </div>
                        )}
                    </div>
                )}

                {suggest.data && suggest.data.options.length > 0 && (
                    <div className="rounded-lg bg-muted/40 p-3">
                        <div className="text-xs font-bold">
                            No contract handy? Start from what actually happened last year:
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {suggest.data.options.map((o) => (
                                <Button key={o.label} size="sm" variant="outline" onClick={() => addSuggestion(o)}>
                                    {o.label} · {money(o.amountCents)}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {drafts.length > 0 && data.alertPeople.length > 0 && (
                    <div>
                        <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            Heads-ups at 75 / 90 / 100% go to
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.alertPeople.map((p) => {
                                const on = people.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() =>
                                            setPeople((xs) => (on ? xs.filter((x) => x !== p.id) : [...xs, p.id]))
                                        }
                                        className={cn(
                                            "rounded-md border px-2.5 py-1 text-[12.5px]",
                                            on
                                                ? "border-foreground/60 bg-foreground/5 font-semibold"
                                                : "text-muted-foreground",
                                        )}
                                    >
                                        {on ? "☑" : "☐"} {p.name}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-1 text-[12px] text-muted-foreground">
                            {people.length === 0
                                ? "Nobody picked = a task in the Office queue."
                                : "They also get the budget letter on the 1st."}
                        </p>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 border-t pt-3">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!ready || create.isPending} onClick={() => create.mutate()}>
                        {create.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                        {chosen.length <= 1 ? "Create budget" : `Create ${chosen.length} budgets`}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
